import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { END_POINTS } from "../Constants";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import IosShareIcon from "@mui/icons-material/IosShare";

const { CANDIDATE_LIST, ADD_PROFILE } = END_POINTS;

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePath, setActivePath] = useState(location.pathname);
  const { role } = useSelector((state) => state.auth.user);

  // Auto collapse on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsCollapsed]);

  // Update active path
  useEffect(() => {
    setActivePath(location.pathname === "/" ? CANDIDATE_LIST : location.pathname);
  }, [location.pathname]);

  // Menu configuration
  const menuConfig = {
    admin: [
      { id: "candidatelist", icon: <PeopleAltIcon />, label: "All Candidates", path: CANDIDATE_LIST },
      { id: "addProfile", icon: <CloudUploadIcon />, label: "Upload & Score", path: ADD_PROFILE },
      { id: "atsMatch", icon: <QueryStatsIcon />, label: "ATS Match Results", path: "/atsMatch" },
      { id: "sharedByMe", icon: <IosShareIcon />, label: "Shared By Me", path: "/shared" },
      { id: "sharedCandidates", icon: <CoPresentIcon />, label: "Candidates for review", path: "/profile" },
    ],
    recruiter: [
      { id: "sharedCandidates", icon: <CoPresentIcon />, label: "Candidates for review", path: "/profile" },
    ],
    viewer: [
      { id: "sharedCandidates", icon: <CoPresentIcon />, label: "Shared With Me", path: "/profile" },
    ],
  };

  const userMenu = menuConfig[role] || [];

  const handleClick = (path) => {
    navigate(path);
    setActivePath(path);
    if (window.innerWidth < 768) setIsCollapsed(true);
  };

  return (
    <div
      className="fixed left-0 top-[60px] h-[calc(100vh-60px)] z-40 bg-gradient-to-b from-slate-50 to-white border-r border-slate-200 transition-all duration-300 flex flex-col"
      style={{ width: isCollapsed ? "60px" : "20%" }}
    >
      {/* Collapse Button */}
      <div className="flex justify-end p-3 border-b border-slate-100">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="group relative w-8 h-8 rounded-lg bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? (
            <ChevronRightIcon className="text-slate-600 group-hover:text-violet-600 transition-colors" fontSize="small" />
          ) : (
            <ChevronLeftIcon className="text-slate-600 group-hover:text-violet-600 transition-colors" fontSize="small" />
          )}
        </button>
      </div>

      {/* Menu List */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="flex flex-col space-y-1">
          {userMenu.map((item) => (
            <SidebarItem
              key={item.id}
              id={item.id}
              icon={item.icon}
              label={item.label}
              isCollapsed={isCollapsed}
              activePath={activePath}
              onClick={() => handleClick(item.path)}
              isActive={activePath === item.path}
            />
          ))}
        </ul>
      </nav>


    </div>
  );
};

const SidebarItem = ({ id, icon, label, isCollapsed, activePath, onClick, isActive }) => {
  const [hovered, setHovered] = useState(false);
  const isItemActive =
    isActive !== undefined
      ? isActive
      : activePath.includes(id.replace("addProfile", "addprofile"));

  return (
    <li
      id={id}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`
        relative group cursor-pointer rounded-xl transition-all duration-300
        ${isCollapsed ? "mx-auto w-11" : "w-full"}
        ${isItemActive 
          ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/30" 
          : "text-slate-700 hover:bg-slate-100"
        }
      `}
    >
      {/* Active indicator line */}
      {isItemActive && !isCollapsed && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full shadow-lg" />
      )}
      
      <div className={`flex items-center gap-3 px-3 py-3 ${isCollapsed ? "justify-center" : "pl-4"}`}>
        {/* Icon container */}
        <div className={`
          flex items-center justify-center transition-all duration-300
          ${isItemActive ? "scale-110" : hovered ? "scale-105" : "scale-100"}
        `}>
          {icon}
        </div>
        
        {/* Label with fade animation */}
        {!isCollapsed && (
          <span className={`
            font-medium text-sm whitespace-nowrap transition-all duration-300
            ${isItemActive ? "font-semibold" : ""}
          `}>
            {label}
          </span>
        )}
      </div>

      {/* Tooltip for collapsed state */}
      {isCollapsed && (
        <div className={`
          absolute left-full ml-2 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg 
          whitespace-nowrap pointer-events-none z-50 shadow-xl
          transition-all duration-200 origin-left
          ${hovered ? "opacity-100 scale-100" : "opacity-0 scale-95"}
        `}>
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
        </div>
      )}

      {/* Hover glow effect */}
      {!isItemActive && hovered && (
        <div className="absolute inset-0 bg-gradient-to-r from-violet-100/50 to-purple-100/50 rounded-xl -z-10" />
      )}
    </li>
  );
};

export default Sidebar;